import { useState, useEffect } from "react";
import { SessionSummary, subscribeToSessionSummary } from "@/store";
import { useAuth } from "@/hooks/auth";
import { logger } from "@/utils";

/**
 * Return type for useSessionSummary hook
 */
export interface UseSessionSummaryReturn {
  /** Session summary data, null if not available */
  insights: SessionSummary | null;
  /** Whether summary is currently being loaded */
  isLoading: boolean;
  /** Any error that occurred during loading */
  error: string | null;
}

/**
 * Hook for managing session summary with real-time updates from Firestore.
 * Listens to changes in the sessionInsights document and updates state accordingly.
 *
 * @param sessionId - The session ID to fetch summary for, or null if no session
 * @returns Session summary state and status
 */
export function useSessionSummary(
  sessionId: string | null
): UseSessionSummaryReturn {
  const { user } = useAuth();
  const [insights, setInsights] = useState<SessionSummary | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Reset state if no session or user
    if (!sessionId || !user) {
      setInsights(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    // Set up real-time listener using store function
    const unsubscribe = subscribeToSessionSummary(
      user.uid,
      sessionId,
      (data) => {
        setInsights(data);
        if (data) {
          logger.info(`Session summary loaded for session ${sessionId}`);
        } else {
          logger.info(`No session summary found for session ${sessionId}`);
        }
        setIsLoading(false);
      },
      (err) => {
        logger.error("Failed to load session summary", err);
        setError("Failed to load session summary");
        setIsLoading(false);
      }
    );

    // Cleanup listener on unmount or when sessionId/user changes
    return () => {
      unsubscribe();
    };
  }, [sessionId, user]);

  return {
    insights,
    isLoading,
    error
  };
}
